import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {Field, ErrorMessage, FormikValues, Formik, Form, FieldMetaProps} from 'formik'
import {ListLoading} from './ListLoading'
import {ID, isNotEmpty, KTSVG} from '../_metronic/helpers'
import {Switch} from './CustomInput'
import {Select, EmailCreatableSelect} from './Select'
import {useQuery} from '@tanstack/react-query'
import {v1_get_request_api} from '../api/get'
import {MultiSelect} from './MultiSelect'
import {SelectFile, types, SelectImage} from './SelectFile'
import {uploadFile} from './fileUtils'
import {MultiInput} from './MultiInput'

type OptionsQuery = {
  queryKey: string[]
  enabled: boolean
  name?: string
}

export type FieldProps = {
  name: string
  label?: string
  required?: boolean //default is false
  full?: boolean //default is false
  disabled?: boolean //default is false
  type?: //default is text
  | 'time'
    | 'text'
    | 'date'
    | 'numeric'
    | 'email'
    | 'password'
    | 'textarea'
    | 'select'
    | 'switch'
    | 'files'
    | 'multi-select'
    | 'multi-input'
    | 'checkbox'
    | 'image'
    | 'useremail'
    | 'link'
  options?: {label: string; value: any}[]
  optionsQuery?: OptionsQuery
  searchFn?: (val: string) => void
  createFn?: (val: string) => void
  selectFn?: (val: string | string[]) => void
  meta?: FieldMetaProps<any>
  limit?: number
  acceptedTypes?: (keyof typeof types)[]
  truthValue?: string
  falseValue?: string
  view?: boolean //default is true
  edit?: boolean //default is true
  table?: boolean //default is false
  sortable?: boolean //default is false
  viewName?: string
  createField?: string
  fields?: any[]
  checkboxLabel?: string
  onchecked?: (e: React.ChangeEvent<HTMLInputElement>, formik: any) => void
}

export type Props = {
  isLoading: boolean
  data: any
  update: (state: any) => Promise<any>
  create: (state: any) => Promise<any>
  setItemIdForUpdate: (id: ID) => void
  refetch: () => void
  schema: any
  fields: FieldProps[] | ((options: {values: any}) => FieldProps[])
}
export type FieldTProps = {
  title: string
  fields: FieldProps[]
}
type TabProps = Omit<Props, 'fields'> & {
  fields: (props: {values: any}) => FieldTProps[]
}

export const CForm: FC<Props> = (props) => {
  ///getting model from url which will be used as upload folder
  const getModelFromUrl = () => {
    const path = window.location.pathname
    const parts = path.split('/').filter(Boolean)
    return parts.includes('edit') || parts.includes('create')
      ? parts[parts.length - 2]
      : parts[parts.length - 1]
  }
  const modelName = getModelFromUrl()

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      props.refetch()
    }
    props.setItemIdForUpdate(undefined)
  }
  const submit = async (values: any, {setSubmitting}: Partial<FormikValues>) => {
    setSubmitting(true)
    try {
      const iFile = Object.keys(values).filter((key) => key.endsWith('_to_upload'))
      for (const file of iFile) {
        const FieldName = file.replace('_to_upload', '')
        const uploaded = await uploadFile(values[file], modelName)
        values[FieldName] = uploaded
        delete values[file]
      }
      if (isNotEmpty(values.id)) {
        await props.update(values)
      } else {
        await props.create(values)
      }
      cancel(true)
    } catch (ex) {
      console.error(ex)
    } finally {
      setSubmitting?.(false)
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={props.schema}
      initialValues={props.data}
      onSubmit={submit}
    >
      {(formik) => {
        const fields =
          typeof props.fields === 'function' ? props.fields({values: formik.values}) : props.fields
        return (
          <>
            <Form className='form' id='kt_modal_add_academic_year_form'>
              <div
                className='d-flex flex-column scroll-y me-n7 pe-7'
                id='kt_modal_add_academic_year_scroll'
                data-kt-scroll='true'
                data-kt-scroll-activate='{default: false, lg: true}'
                data-kt-scroll-max-height='auto'
                data-kt-scroll-dependencies='#kt_modal_add_academic_year_header'
                data-kt-scroll-wrappers='#kt_modal_add_academic_year_scroll'
                data-kt-scroll-offset='300px'
              ></div>
              <div className='row'>
                {fields?.map((field: any, index: any) => (
                  <CFields key={index} field={field} formik={formik} />
                ))}
              </div>
              <div className='text-center pt-15'>
                <button
                  type='reset'
                  onClick={() => cancel()}
                  className='btn btn-light me-3'
                  data-kt-academic_years-modal-action='cancel'
                  disabled={formik.isSubmitting || props.isLoading}
                >
                  Discard
                </button>

                <button
                  type='submit'
                  className='btn btn-primary'
                  data-kt-academic_years-modal-action='submit'
                  disabled={
                    props.isLoading || formik.isSubmitting || !formik.isValid || !formik.touched
                  }
                  data-kt-indicator={formik.isSubmitting ? 'on' : 'off'}
                >
                  <span className='indicator-label'>Submit</span>
                  <span className='indicator-progress'>
                    Please wait...{' '}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                </button>
              </div>
            </Form>
            {(formik.isSubmitting || props.isLoading) && <ListLoading />}
          </>
        )
      }}
    </Formik>
  )
}

export const CTabForm: FC<TabProps> = (props) => {
  const [step, setStep] = useState(0)

  const getModelFromUrl = () => {
    const path = window.location.pathname
    const parts = path.split('/').filter(Boolean)
    return parts.includes('edit') || parts.includes('create')
      ? parts[parts.length - 2]
      : parts[parts.length - 1]
  }
  const modelName = getModelFromUrl()

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) props.refetch()
    props.setItemIdForUpdate(undefined)
  }

  const submit = async (values: any, {setSubmitting}: Partial<FormikValues>) => {
    setSubmitting?.(true)
    try {
      const iFile = Object.keys(values).filter((key) => key.endsWith('_to_upload'))
      for (const file of iFile) {
        const FieldName = file.replace('_to_upload', '')
        const uploaded = await uploadFile(values[file], modelName)
        values[FieldName] = uploaded
        delete values[file]
      }

      if (isNotEmpty(values.id)) {
        await props.update(values)
      } else {
        await props.create(values)
      }

      cancel(true)
    } catch (ex) {
      console.error(ex)
    } finally {
      setSubmitting?.(false)
    }
  }

  return (
    <Formik
      enableReinitialize={false}
      initialValues={props.data}
      onSubmit={submit}
    >
      {(formik) => {
        const levels =
          typeof props.fields === 'function' ? props.fields({values: formik.values}) : props.fields
        const current = levels[step]

        return (
          <>
            <Form className='form relative z-10'>
              <div
                className='d-flex flex-column scroll-y me-n7 pe-7'
                id='kt_modal_add_academic_year_scroll'
                data-kt-scroll='true'
                data-kt-scroll-activate='{default: false, lg: true}'
                data-kt-scroll-max-height='auto'
                data-kt-scroll-dependencies='#kt_modal_add_academic_year_header'
                data-kt-scroll-wrappers='#kt_modal_add_academic_year_scroll '
                data-kt-scroll-offset='300px'
              ></div>
              <div className='d-flex justify-content-center mb-10 gap-4 border-bottom pb-2'>
                {levels.map((lvl, index) => (
                  <div
                    key={index}
                    className={`cursor-pointer px-3 py-2 fw-bold position-relative ${
                      index === step
                        ? 'text-primary'
                        : index < step
                        ? 'text-muted'
                        : 'text-gray-400'
                    }`}
                    onClick={async () => {
                      if (index === step) return
                      // Validate current step before moving
                      const errors = await formik.validateForm()
                      const hasErrors = Object.keys(errors).some((key) => {
                        const fieldInCurrentStep = current.fields?.find((f: any) => f.name === key)
                        return !!fieldInCurrentStep
                      })
                      !hasErrors
                        ? setStep(index)
                        : formik.setTouched(
                            Object.keys(errors).reduce((acc, key) => {
                              acc[key] = true
                              return acc
                            }, {} as any)
                          )
                    }}
                    style={{
                      borderBottom: index === step ? '3px solid #3699FF' : '3px solid transparent',
                    }}
                  >
                    {lvl.title}
                  </div>
                ))}
              </div>
              <div className='row'>
                {current.fields.map((field: any, i: number) => (
                  <CFields key={`${step}-${field.name}`} field={field} formik={formik} />
                ))}
              </div>
              <div className='d-flex justify-content-between pt-10'>
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={() => (step > 0 ? setStep(step - 1) : cancel())}
                  disabled={formik.isSubmitting || props.isLoading}
                >
                  {step > 0 ? 'Previous' : 'Discard'}
                </button>

                {step < levels.length - 1 ? (
                  <button
                    type='button'
                    className='btn btn-primary'
                    onClick={async () => {
                      const errors = await formik.validateForm()
                      const hasErrors = Object.keys(errors).some((key) =>
                        current.fields.find((f: any) => f.name === key)
                      )
                      !hasErrors
                        ? setTimeout(() => setStep(step + 1), 0)
                        : formik.setTouched(
                            Object.keys(errors).reduce((acc, key) => {
                              acc[key] = true
                              return acc
                            }, {} as any)
                          )
                    }}
                    disabled={formik.isSubmitting || props.isLoading}
                  >
                    Continue
                  </button>
                ) : (
                  <button
                    type='submit'
                    className='btn btn-primary'
                    disabled={
                      formik.isSubmitting || props.isLoading || !formik.isValid || !formik.touched
                    }
                    data-kt-indicator={formik.isSubmitting ? 'on' : 'off'}
                  >
                    <span className='indicator-label'>Submit</span>
                    <span className='indicator-progress'>
                      Please wait...{' '}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  </button>
                )}
              </div>
            </Form>

            {(formik.isSubmitting || props.isLoading) && <ListLoading />}
          </>
        )
      }}
    </Formik>
  )
}

export const CFields: FC<{field: FieldProps; formik: any}> = ({field, formik}) => {
  const {data: result} = useQuery({
    queryKey: field.optionsQuery?.queryKey || [],
    queryFn: v1_get_request_api,
    enabled: field.optionsQuery?.enabled || false,
  })

  const [options, setOptions] = useState<any[]>(field.options || [])

  useEffect(() => {
    if (result && field.optionsQuery) {
      setOptions(
        result.entities.map((item: any) => {
          if (field.optionsQuery?.name) {
            const names = field.optionsQuery.name.split('.')
            let value = item
            names.forEach((n: any) => {
              if (value) value = value[n]
            })
            return {
              label: value,
              value: item.id,
            }
          }
          return {
            label: item.name,
            value: item.id,
          }
        })
      )
    }
  }, [result])
  const value = formik.values[field.name] || formik.getFieldMeta(field.name).value
  const selected = useMemo(
    () =>
      options?.find((o) => o.value === value) || {
        value,
        label: value,
      },
    [value]
  )

  const multiSelected =
    Array.isArray(formik.values[field.name]) && options
      ? formik.values[field.name].map((val: any) => {
          const found = options.find((o) => o.value === val)
          return found || {value: val, label: val}
        })
      : []

      const selectedFile = useMemo(() => {
        return formik.values[field.name] || formik.getFieldMeta(field.name).value
      }, [formik.values[field.name]])

      const handleFileSelect = useCallback(
        (file: File | null) => {
          formik.setFieldValue(field.name + '_to_upload', file)
          formik.setFieldValue(field.name, file)
        },
        [field.name, formik]
      )
  return field.type === 'multi-select' ? (
    <div className={'mb-5 fv-row ' + (!field.full ? 'col-lg-6' : '')}>
      <label className={'form-label mb-3 ' + (field.required ? 'required' : '')}>
        {field.label}
      </label>
      <MultiSelect
        name={field.name}
        placeholder={'select multiple opt...'}
        options={options || []}
        selected={multiSelected}
        onSelect={(values: {value: any; label: string}[]) => {
          const selectedValues = values.map((v: any) => v.value)
          field.selectFn
            ? field.selectFn(selectedValues)
            : formik.setFieldValue(field.name, selectedValues)
        }}
        error={!!formik.getFieldMeta(field.name).error}
        createFn={
          field.createField
            ? (val: string) => formik.setFieldValue(field.createField, val)
            : undefined
        }
      />
      <div className='text-danger mt-2'>
        <ErrorMessage name={field.name} />
      </div>
    </div>
  ) : field.type === 'select' ? (
    <div className={'mb-5 fv-row ' + (!field.full ? 'col-lg-6' : '')}>
      <label className={'form-label mb-3 ' + (field.required ? 'required' : '')}>
        {field.label}
      </label>
      <Select
        name={field.name}
        placeholder={`Select ${field.label}...`}
        options={options}
        selected={selected}
        onSelect={(val: {value: any; label: string}) =>
          field.selectFn ? field.selectFn(val.value) : formik.setFieldValue(field.name, val.value)
        }
        error={!!formik.getFieldMeta(field.name).error}
        createFn={
          field.createField
            ? (val: string) => formik.setFieldValue(field.createField, val)
            : undefined
        }
      />
      <div className='text-danger mt-2'>
        <ErrorMessage name={field.name} />
      </div>
    </div>
  ) : field.type === 'useremail' ? (
    <div className={'mb-5 fv-row ' + (!field.full ? 'col-lg-6' : '')}>
      <label className={'form-label mb-3 ' + (field.required ? 'required' : '')}>
        {field.label}
      </label>
      <EmailCreatableSelect
        name={field.name}
        placeholder={`Search ${field.label}...`}
        formik={formik}
        selectFn={field.selectFn}
        createField={field.createField}
      />
      <div className='text-danger mt-2'>
        <ErrorMessage name={field.name} />
      </div>
    </div>
  ) : field.type === 'files' ? (
    <div className={'mb-5 fv-row ' + (!field.full ? 'col-lg-6' : '')}>
      <label className={'form-label mb-3 ' + (field.required ? 'required' : '')}>
        {field.label}
      </label>
      <SelectFile
        label={field.label}
        onSelect={handleFileSelect}
        disabled={field.disabled}
        error={!!formik.getFieldMeta(field.name).error}
        acceptedTypes={field.acceptedTypes}
      />
      <div className='text-danger mt-2'>
        <ErrorMessage name={field.name} />
      </div>
    </div>
  ) : field.type === 'image' ? (
    <div className={'mb-5 fv-row ' + (!field.full ? 'col-lg-6' : '')}>
      <label className={'form-label mb-3 ' + (field.required ? 'required' : '')}>
        {field.label}
      </label>
      <SelectImage
        label={field.label}
        onSelect={handleFileSelect}
        file={selectedFile}
        disabled={field.disabled}
        error={!!formik.getFieldMeta(field.name).error}
      />
      <div className='text-danger mt-2'>
        <ErrorMessage name={field.name} />
      </div>
    </div>
  ) : field.type === 'multi-input' ? (
    <div className={'mb-5 fv-row ' + (!field.full ? 'col-lg-6' : '')}>
      <label className={'form-label mb-3 ' + (field.required ? 'required' : '')}>
        {field.label}
      </label>
      <span
        className='svg-icon btn-icon svg-icon-2 cursor-pointer ml-2'
        onClick={() => {
          const newItem = Object.fromEntries(
            (field.fields || []).map((f) => [
              f.name.includes('[') ? f.name.split('[').pop()?.split(']')[0] : f.name,
              f.type === 'switch'
                ? false
                : f.type === 'multi-select'
                ? []
                : f.type === 'files'
                ? null
                : '',
            ])
          )
          formik.setFieldValue(field.name, [...(formik.values[field.name] || []), newItem])
        }}
      >
        <KTSVG
          path='/media/icons/duotune/general/gen041.svg'
          className='svg-icon-primary svg-icon-1hx'
        />
      </span>
      <MultiInput
        field={{
          ...field,
          value: formik.values[field.name] || [],
          onChange: (val: any) => formik.setFieldValue(field.name, val),
          fields: field.fields || [],
        }}
        formik={formik}
      />
      <div className='text-danger mt-2'>
        <ErrorMessage name={field.name} />
      </div>
    </div>
  ) : field.type === 'checkbox' ? (
    <div className={`col-md-${field.full ? '12' : '6'} mb-4`} key={field.name}>
      <label className='form-label fw-bold mb-3 d-block'>{field.label}</label>
      {Array.isArray(options) && options.length > 0 ? (
        options.map((opt: any) => (
          <div key={opt.value} className='form-check form-check-custom form-check-solid mb-2'>
            <input
              type='checkbox'
              className='form-check-input'
              id={`${field.name}_${opt.value}`}
              name={field.name}
              value={opt.value}
              checked={formik.values[field.name]?.includes(opt.value)}
              onChange={() => {
                const selected: any[] = formik.values[field.name] || []
                if (selected.includes(opt.value)) {
                  formik.setFieldValue(
                    field.name,
                    selected.filter((v) => v !== opt.value)
                  )
                } else {
                  formik.setFieldValue(field.name, [...selected, opt.value])
                }
              }}
            />
            <label className='form-check-label' htmlFor={`${field.name}_${opt.value}`}>
              {opt.label}
            </label>
          </div>
        ))
      ) : (
        <div className='form-check form-check-custom form-check-solid'>
          <input
            type='checkbox'
            className='form-check-input'
            id={field.name}
            name={field.name}
            checked={formik.values[field.name] || false}
            onChange={(e) => {
              formik.setFieldValue(field.name, e.target.checked)
              if (typeof field.onchecked === 'function') {
                field.onchecked(e, formik)
              }
            }}
          />
         
        </div>
      )}

      <div className='text-danger mt-2'>
        <ErrorMessage name={field.name} />
      </div>
    </div>
  ) : field.edit !== false ? (
    <div className={'mb-5 fv-row ' + (!field.full ? 'col-lg-6' : '')}>
      <label className={'form-label mb-3 ' + (field.required ? 'required' : '')}>
        {field.label}
      </label>

      <Field
        type={field.type}
        className='form-control form-control-lg form-control-solid'
        name={field.name}
        as={field.type === 'textarea' ? 'textarea' : undefined}
        component={field.type === 'switch' ? Switch : undefined}
        disabled={field.disabled}
      />
      <div className='text-danger mt-2'>
        <ErrorMessage name={field.name} />
      </div>
    </div>
  ) : null
}
